import { EyeOutlined, SearchOutlined } from "@ant-design/icons";
import type { TableProps } from 'antd';
import dayjs from "dayjs";
import { Button, Col, ConfigProvider, DatePicker, Flex, Input, Layout, Row, Skeleton, Table, notification, Upload, Modal, Select, Drawer, Tabs, Tooltip } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { useEffect, useRef, useState } from "react";
import { LoaderSpinner } from "../../../components/common/Loader";
import { get835PaymentFiles, getClients, getPayment835FileStatuses, uploadPayment835File } from "../../../service/httpsCalls";
import { UploadOutlined } from '@ant-design/icons';
import { createTableConfig } from "../tableConfig";
import Payment835FileDetails, { Payment835FileChildRef } from "./Payment835FileDetails";
const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

type NotificationType = 'success' | 'info' | 'warning' | 'error';

interface ValidationResult {
    isValid: boolean,
    message: string
};

interface ClientList {
    clientId: number,
    companyName: string
};

interface StatusList {
    id: number,
    name: string
};

interface FormFields {
    file: any | null,
    additionalFile: any | null,
    uploadFileClientId: number | null,
    show: boolean,
    loader: boolean,
    fileList: any[],
    additionalFileList: any[]
};

export default function Payment835Files() {
    const childRef = useRef<Payment835FileChildRef>(null);
    const [api, contextHolder] = notification.useNotification();

    const [tableColumns, setTableColumns] = useState([]);
    const [fileInfoList, setfileInfoList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortedInfo, setSortedInfo] = useState<SorterResult<any>>({});
    const [menuVisible, setMenuVisible] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [filterLoader] = useState(false);

    const [clientList, setClientList] = useState<ClientList[]>([]);
    const [statusList, setStatusList] = useState<StatusList[]>([]);

    const [clientId, setClientId] = useState<number | null>();
    const [statusId, setStatusId] = useState<number | null>();
    const [fileName, setfileName] = useState<string | null>();
    const [startDate, setStartDate] = useState<string | any>('');
    const [endDate, setEndDate] = useState<string | any>('');

    const [showModal, setModal] = useState<boolean>(false);
    const [modalData, setModalData] = useState<any>({
        edit: false,
        data: null,
    })

    const [fileUploadForm, setFileUploadForm] = useState<FormFields>({
        file: null,
        additionalFile: null,
        uploadFileClientId: null,
        show: false,
        loader: false,
        fileList: [],
        additionalFileList: []
    })

    // drawer settings
    const screenWidth = window.innerWidth;
    const drawerScreenWidth = screenWidth * 0.7;
    const defaultWidth = 1000;
    const [drawerConfig] = useState({
        title: '835 Payment File',
        width: Math.max(drawerScreenWidth, defaultWidth)
    })

    const getClientList = async () => {
        var clients = await getClients();
        const allowedClients = [5, 10, 13, 16];

        const options = clients
            .filter((entry: ClientList) => allowedClients.includes(entry.clientId))
            .map((entry: ClientList) => ({
                clientId: entry.clientId,
                companyName: entry.companyName
            }))
            .sort((a: ClientList, b: ClientList) => a.companyName.localeCompare(b.companyName));

        setClientList(options);
    };

    const getStatusList = async () => {
        var statuses = await getPayment835FileStatuses();

        const options = statuses
            .map((entry: StatusList) => ({
                id: entry.id,
                name: entry.name
            }));

        setStatusList(options);
    };

    const openNotificationWithIcon = (type: NotificationType, message: string, description: string) => {
        api[type]({
            message: message,
            description: description
        });
    };

    function resetFilters() {
        setClientId(null);
        setStatusId(null);
        setStartDate('');
        setEndDate('');
        setfileName(null);
        setFileUploadForm({
            ...fileUploadForm,
            uploadFileClientId: null
        });
        getfileInfoList(true);
    }

    function clientSelected(e: any) {
        if (e) {
            setClientId(e);
            setFileUploadForm({
                ...fileUploadForm,
                uploadFileClientId: e
            });
        }
    }

    function statusSelected(value?: number) {
        setStatusId(value);
    }

    function uploadFileClientSelected(e: any) {
        if (e) {
            setFileUploadForm({
                ...fileUploadForm,
                uploadFileClientId: e
            });
        }
    }

    function getfileInfoList(isFromReset?: boolean) {
        setLoading(true);
        setfileInfoList([]);
        get835PaymentFiles({
            "pageIndex": currentPage,
            "pageSize": pageSize,
            "sortField": sortedInfo.order ? sortedInfo.field : null,
            "sortDescending": sortedInfo.order == 'descend',
            "clientId": !isFromReset && clientId ? clientId : 0,
            "fromUploadedDate": !isFromReset && startDate && startDate != '' ? dayjs(startDate).format('YYYY-MM-DD') : null,
            "toUploadedDate": !isFromReset && endDate && endDate != '' ? dayjs(endDate).format('YYYY-MM-DD') : null,
            "fileName": !isFromReset && fileName ? fileName : null,
            "statusId": !isFromReset && statusId ? statusId : 0

        }, (res: any) => {
            if (res !== 'error') {
                !showTable && setShowTable(true);

                if (res.columnConfigs && !tableColumns.length) {
                    let cols = createTableConfig(res.columnConfigs);

                    cols = [...cols,
                    {
                        title: 'Actions',
                        dataIndex: 'actions',
                        dataType: 'action',
                        hidden: false,
                        fixed: false,
                        sorter: false,
                        filter: false,
                        width: 100,
                        render: (text: any, record: any) => {
                            return (
                                <Button
                                    onClick={() => {
                                        setModal(true);
                                        setModalData({
                                            edit: true,
                                            data: record
                                        });
                                    }}
                                    style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: 'rgba(0, 0, 0, 0.88)' }}
                                    type="primary"
                                    icon={<EyeOutlined />}
                                />
                            );
                        }
                    }
                    ]
                    setTableColumns(cols);
                }

                res.columnConfigs.map((entry: any) => ({ item: entry.title, checked: !entry.hidden }))
                setfileInfoList(res.items);
                setLoading(false)
                setTotalPages(Math.ceil(res.totalRecords / (pageSize / pageSize)));
            } else {
                setfileInfoList([]);
                setLoading(false)
                setTotalPages(1);
            }

        })
    }

    function search() {
        if (!clientId) {
            openNotificationWithIcon("warning", "Please Select a Client", "");
            return;
        }

        if (pageSize == 0) {
            setPageSize(10);
        } else {
            currentPage == 1 ? getfileInfoList() : setCurrentPage(1);
        }
    }

    const [tableScrollY, setTableScrollY] = useState<number>();

    useEffect(() => {
        setPageSize(10);
        getClientList();
        getStatusList();
        document.title = "835 Payment Files";

        const compute = () => {
            const height = window.innerHeight - 520;
            setTableScrollY(height > 0 ? height : undefined);
        };

        compute();
        window.addEventListener("resize", compute);
        return () => window.removeEventListener("resize", compute);
    }, []);

    useEffect(() => {
        if (pageSize != 0)
            getfileInfoList();
    }, [currentPage, pageSize, sortedInfo]);

    useEffect(() => {
        if (!showModal) {
            setModalData({
                edit: false,
                data: null
            })
        }
    }, [showModal])

    const handleTableChange: TableProps<any>['onChange'] = (pagination, filters, sorter, extra) => {
        setCurrentPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);
        setTotalPages(pagination.total ?? 100);
        setSortedInfo(sorter as SorterResult<any>);
    };

    const isValid835PaymentFile = (file: File, clientId: number | null): ValidationResult => {
        const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;

        const allowedExtensions: Record<number, string[]> = {
            16: [".835"],
            13: [".835"],
            5: [".835", ".dat"],
            10: [".edi"],
        };

        const clientAllowedExtensions = allowedExtensions[clientId] || [];
        const isExtensionValid = clientAllowedExtensions.includes(extension);

        if (!isExtensionValid) {
            return {
                isValid: false,
                message: `File type ${extension} is not allowed for the selected client.`,
            };
        }

        return {
            isValid: true,
            message: "File is valid.",
        };
    };

    function upload835PaymentFile({ file, fileList }) {
        if (file?.status == "removed") {
            setFileUploadForm({
                ...fileUploadForm,
                file: null,
                fileList: []
            });
            return;
        }

        if (!fileUploadForm.uploadFileClientId) {
            openNotificationWithIcon("warning", "Please select a client.", "");
            return;
        }

        const result = isValid835PaymentFile(file, fileUploadForm.uploadFileClientId);

        if (!result.isValid) {
            openNotificationWithIcon("warning", result.message, "");
            setFileUploadForm({
                ...fileUploadForm,
                fileList: []
            });
            return Upload.LIST_IGNORE;
        } else {
            setFileUploadForm({
                ...fileUploadForm,
                file: file.originFileObj || file,
                fileList: fileList
            });
        }
    }

    function uploadAdditionalFile({ file, fileList }) {
        if (file?.status == "removed") {
            setFileUploadForm({
                ...fileUploadForm,
                additionalFile: null,
                additionalFileList: fileList
            });
            return;
        }
        const allowedTypes = [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
            "text/plain", // .txt
            "text/csv", // .csv
            "application/pdf", // .pdf
            "application/msword", // .doc
        ];

        if (!allowedTypes.includes(file.type)) {
            openNotificationWithIcon("warning", `File type ${file.type} is not allowed for additional file.`, "");
            return Upload.LIST_IGNORE;
        } else {
            setFileUploadForm({
                ...fileUploadForm,
                additionalFile: file.originFileObj || file,
                additionalFileList: fileList
            });
        }
    }

    function saveFile() {
        if (!fileUploadForm.file) {
            openNotificationWithIcon("warning", "No file selected. Please choose an 835 payment file to upload.", "");
            return;
        }
        setFileUploadForm({ ...fileUploadForm, loader: true })
        uploadPayment835File({ clientId: fileUploadForm.uploadFileClientId, paymentFile: fileUploadForm.file, additionalFiles: fileUploadForm.additionalFileList }, (res: any) => {
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!'
                openNotificationWithIcon("warning", errorMsg, "");
                setFileUploadForm({ ...fileUploadForm, loader: false })
            } else {
                openNotificationWithIcon("success", "File uploaded!!", "");
                setFileUploadForm({ ...fileUploadForm, show: false, loader: false, file: null, fileList: [] })
                currentPage == 1 ? getfileInfoList() : setCurrentPage(1);
            }

        })
    }

    const handleCallback = (data: any) => {
        if (data?.isInvalid) {
            openNotificationWithIcon("warning", data.errorMessage, "");
        } else if (data.showMessage && data.message) {
            openNotificationWithIcon("success", data.message, "");
        }
    }

    function closeModal() {
        setModal(false);
        setModalData({
            edit: false,
            data: null
        })
    }

    const drawerTitle = <Tooltip title={drawerConfig.title}>
        <div
            style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
                maxWidth: '800px'
            }}
        >
            {drawerConfig.title}
        </div>
    </Tooltip>


    return <ConfigProvider theme={{
        components: {
            Layout: { bodyBg: "#eff1f5", siderBg: "#fff" }
        }
    }} >
        {contextHolder}
        <Layout style={{
            padding: '20px',
            marginBottom: '5px',
            marginTop: '5px',
        }}>
            <Drawer title={drawerTitle} destroyOnClose={true} maskClosable={false} onClose={closeModal} open={showModal} width={drawerConfig.width} styles={{ body: { background: '#eff1f5' } }}>
                <div>
                    {modalData.edit &&
                        <Payment835FileDetails ref={childRef} modalData={modalData} parentCallback={handleCallback} />
                    }
                </div>
            </Drawer>
            <Modal title="Upload File" open={fileUploadForm.show}
                afterClose={() => setFileUploadForm({ ...fileUploadForm, show: false, file: null, fileList: [], uploadFileClientId: clientId, additionalFile: null, additionalFileList: [] })}
                onCancel={() => setFileUploadForm({ ...fileUploadForm, show: false, file: null, fileList: [], uploadFileClientId: clientId, additionalFile: null, additionalFileList: [] })}
                onOk={() => saveFile()}
                confirmLoading={fileUploadForm.loader}
                width={500}
                maskClosable={false}
                className="customModal"
            >
                <div>
                    <Row style={{ paddingBottom: '15px' }}>
                        <Col span={12}>
                            Client:
                        </Col>
                        <Col span={12}>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="Select a Client"
                                onChange={(e: any) => uploadFileClientSelected(e)}
                                value={fileUploadForm.uploadFileClientId}
                            >
                                {clientList && clientList.map((item: ClientList) => (
                                    <Option key={item.clientId}>{item.companyName}</Option>
                                ))}
                            </Select>
                        </Col>
                    </Row>
                    <Row style={{ paddingBottom: '15px' }}>
                        <Col span={12}>
                            835 Payment File:
                        </Col>
                        <Col span={12}>
                            <Upload maxCount={1} fileList={fileUploadForm.fileList} onChange={upload835PaymentFile} beforeUpload={() => false} accept=".835,.edi,.dat">
                                <Button style={{ width: '100%' }}>
                                    <UploadOutlined /> Click to Upload
                                </Button>
                            </Upload>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={12}>
                            Additional Files:
                        </Col>
                        <Col span={12}>
                            <Upload fileList={fileUploadForm.additionalFileList} onChange={uploadAdditionalFile} beforeUpload={() => false} accept=".txt,.xlsx,.csv,.pdf,.doc">
                                <Button style={{ width: '100%' }}>
                                    <UploadOutlined /> Click to Upload
                                </Button>
                            </Upload>
                        </Col>
                    </Row>
                </div>
            </Modal>

            <h3 style={{ textTransform: 'uppercase', marginTop: '0px' }}>835 Payment Files</h3>
            <Flex justify='space-between' style={{ padding: "1rem", alignItems: "center", background: '#fff' }}>
                <label><SearchOutlined />&nbsp;Search</label>
                <Button onClick={e => setFileUploadForm({ ...fileUploadForm, show: true, loader: false })} style={{ marginLeft: '5px' }} type="primary" icon={<UploadOutlined />}>
                    Upload File
                </Button>

            </Flex>
            <Skeleton loading={filterLoader} active>
                <Row style={{ padding: '1rem', paddingTop: "0px", background: '#fff' }}>
                    <Col span={6}>
                        <Select
                            style={{ width: '90%' }}
                            placeholder="Select a Client"
                            onChange={(e) => clientSelected(e)}
                            value={clientId}
                        >
                            {clientList && clientList.map((item: ClientList) => (
                                <Option key={item.clientId}>{item.companyName}</Option>
                            ))}
                        </Select>
                    </Col>

                    <Col span={6}>
                        <Input value={fileName ?? ''}
                            placeholder="File Name"
                            style={{ width: '90%' }}
                            allowClear onChange={(e) => setfileName(e?.target?.value)} />
                    </Col>

                    <Col span={12}>
                        <RangePicker
                            placeholder={["From Uploaded Date", "To Uploaded Date"]}
                            style={{ width: '100%' }}
                            format="MM/DD/YYYY"
                            value={[startDate, endDate]}
                            onChange={(dates: any) => {
                                if (dates) {
                                    setStartDate(dates[0]);
                                    setEndDate(dates[1]);
                                } else {
                                    setStartDate('');
                                    setEndDate('');
                                }
                            }}
                        />
                    </Col>
                </Row>
                <Row style={{ padding: '1rem', paddingTop: "0px", background: '#fff' }}>
                    <Col span={6}>
                        <Select
                            style={{ width: '90%' }}
                            placeholder="Select a Status"
                            value={statusId ?? undefined}
                            allowClear
                            onChange={(value) => statusSelected(value)}
                        >
                            {statusList?.map((item: StatusList) => (
                                <Option key={item.id} value={item.id}>
                                    {item.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                <Flex justify='end' style={{ marginBottom: "2rem", padding: "1rem", paddingTop: "0px", background: '#fff' }} gap='small'>
                    <Button onClick={search} loading={loading} style={{ marginLeft: '5px' }} type="primary">
                        Search
                    </Button>
                    <Button onClick={resetFilters} style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', marginLeft: '5px', color: 'rgba(0, 0, 0, 0.88)' }} type="primary">
                        Reset
                    </Button>
                </Flex>
            </Skeleton>

            {showTable && <Table style={{ background: "#fff" }}
                scroll={{
                    y: fileInfoList?.length > 0 ? tableScrollY : undefined,
                }}
                dataSource={fileInfoList}
                pagination={{
                    defaultCurrent: 1,
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalPages,
                    showTotal: (total, range) => `${range[0] || 0}-${range[1] || 1} of ${total} items`,
                    showSizeChanger: true,
                }}
                columns={tableColumns}
                onChange={handleTableChange}
                size="small"
                rowKey="fileName"
                loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: loading }}
                onHeaderRow={(columns, index) => {
                    return {
                        onContextMenu: (event) => {
                            event.preventDefault();
                            if (!menuVisible) {
                                document.addEventListener(`click`, function onClickOutside() {
                                    setMenuVisible(false);
                                    document.removeEventListener(`click`, onClickOutside);
                                });
                            }
                            setMenuVisible(true);
                        }
                    };
                }}
            />

            }
        </Layout>
    </ConfigProvider>
}